import { functions } from "@app/configs/firebase.js";
import { httpsCallable } from "firebase/functions";

const CALLABLE_NAME = "v8AssistantChat";
const TIMEOUT_MS = 120000;
const AGENT_TIMEOUT_MS = 300000;

function getCallable(timeout = TIMEOUT_MS) {
    if (!functions) {
        throw new Error("The AI Assistant is not available offline.");
    }
    return httpsCallable(functions, CALLABLE_NAME, { timeout });
}

/** @returns {Promise<{ plan: string, limit: number, used: number, remaining: number, agent: { allowed: boolean, creditsUsed?: number, creditsLimit?: number, creditsRemaining?: number } }>} */
export async function getAssistantStatus() {
    const result = await getCallable()({ action: "status" });
    return result.data;
}

/**
 * Ask a question and stream the answer.
 * @param {object} payload  { question, history, projectContext, projectId, mode }
 * @param {object} handlers
 * @param {(text: string) => void} [handlers.onText]
 * @param {(citation: { url: string, title: string, citedText: string }) => void} [handlers.onCitation]
 * @param {AbortSignal} [handlers.signal]
 * @returns {Promise<{ text: string, citations: Array, remaining: number, limit: number, plan: string }>}
 */
export async function streamAssistantAnswer(payload, { onText, onCitation, signal } = {}) {
    const { stream, data } = await getCallable().stream(payload, { signal });
    for await (const chunk of stream) {
        if (chunk?.type === "text") {
            onText?.(chunk.text);
        } else if (chunk?.type === "citation") {
            onCitation?.(chunk);
        }
    }
    return data;
}

function getPluginCallable() {
    if (!functions) {
        throw new Error("The AI Assistant is not available offline.");
    }
    return httpsCallable(functions, "v8AgentPlugin", { timeout: TIMEOUT_MS });
}

/**
 * Add the AI Agent plugin to the account's subscription (billing owner only).
 * @returns {Promise<{ added: boolean, agent: object }>}
 */
export async function addAgentPlugin() {
    const result = await getPluginCallable()({ action: "add" });
    return result.data;
}

/**
 * Set the monthly overage limit in whole USD (0 turns overage off).
 * @returns {Promise<{ agent: object }>}
 */
export async function setAgentOverageCap(capUsd) {
    const result = await getPluginCallable()({ action: "setOverageCap", capUsd });
    return result.data;
}

/**
 * Run one Agent model step and stream its text and tool calls.
 * @param {object} payload  { mode: "agent", agentModel: "standard"|"deep", messages, projectContext?, chatId, turnId, projectId }
 * @param {object} handlers
 * @param {(text: string) => void} [handlers.onText]
 * @param {(toolUse: { id: string, name: string }) => void} [handlers.onToolUse]
 * @param {AbortSignal} [handlers.signal]
 * @returns {Promise<{ content: Array, stopReason: string, userContent: Array|null, step: number, credits: object }>}
 */
export async function streamAgentStep(payload, { onText, onToolUse, signal } = {}) {
    const { stream, data } = await getCallable(AGENT_TIMEOUT_MS).stream(payload, { signal });
    for await (const chunk of stream) {
        if (chunk?.type === "text") {
            onText?.(chunk.text);
        } else if (chunk?.type === "tool_use") {
            onToolUse?.(chunk);
        }
    }
    return data;
}
